import type { ReactNode } from "react";
import { Spin } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpOutlined,
  BorderOuterOutlined,
  CloudOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { SCHEMA_PAGE_PATH } from "../logic/schemaSetup";

const ICONS: Record<"offline" | "error" | "empty", { icon: ReactNode; tone: string }> = {
  offline: { icon: <CloudOutlined />, tone: "bg-warn-tint text-warn-ink" },
  error: { icon: <ExclamationCircleOutlined />, tone: "bg-danger-tint text-danger-ink" },
  empty: { icon: <BorderOuterOutlined />, tone: "bg-brand-tint text-brand-deep-ink" },
};

/**
 * Пътят към „Схема на заведението", когато няма маси (и има право за нея —
 * `canLinkToSchema`). `scope`:
 * - „restaurant" — в цялата схема няма нито една маса (`showsSchemaSetup`):
 *   вместо празното платно/таблица, самостоятелна карта като празния екран на
 *   самата схема без район (RestaurantSchemaPage);
 * - „region" — показаният район е без маси: същото съдържание вътре в
 *   платното/таблицата, без втора рамка.
 */
export const SchemaSetupNotice = ({
  scope = "restaurant",
  regionId,
}: {
  scope?: "restaurant" | "region";
  // Празният район — схемата се отваря направо на него.
  regionId?: number;
}) => {
  const { t } = useTranslation();
  const textKey = scope === "region" ? "reservations.noTablesInRegion" : "reservations.noTables";
  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center ${
        scope === "region" ? "py-10" : "rounded-[14px] border border-dashed border-shade-4 bg-surface py-14"
      }`}
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-brand-tint text-[30px] text-brand-deep-ink">
        <BorderOuterOutlined />
      </span>
      <div>
        <div className="text-[20px] font-bold leading-[1.2] tracking-[-0.4px] text-ink">
          {t(`${textKey}.title`)}
        </div>
        <div className="mx-auto mt-2 max-w-[440px] text-[13.5px] font-medium leading-[1.5] text-body">
          {t(`${textKey}.description`)}
        </div>
      </div>
      <Link
        to={SCHEMA_PAGE_PATH}
        state={regionId === undefined ? undefined : { regionId }}
        // На най-тесните телефони надписът се пренася, вместо да излиза от картата.
        // Цветовете и прехода са с `!`: глобалното `a` правило на antd (cssinjs,
        // извън Tailwind layer-ите) иначе прави фона прозрачен и текста цвят на линк.
        className="flex min-h-[42px] max-w-full items-center justify-center gap-2 rounded-[10px] !bg-brand px-5 py-2 text-center text-[14px] font-bold leading-[1.25] !text-white !transition-colors duration-150 hover:!bg-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
      >
        {t("reservations.noTables.action")}
        <ArrowUpOutlined className="shrink-0 rotate-45" />
      </Link>
    </div>
  );
};

/**
 * Състоянията на страницата без схема: зареждане, без интернет („Недостъпно
 * без интернет" — касата няма резервациите), грешка, липсваща схема.
 */
const PageNotice = (
  props: { tone: "loading" } | { tone: "offline" | "error" | "empty"; title: string; hint: string },
) => {
  if (props.tone === "loading") {
    return (
      <div className="flex justify-center rounded-[14px] border border-shade-2 bg-surface p-12">
        <Spin size="large" />
      </div>
    );
  }
  const { icon, tone } = ICONS[props.tone];
  return (
    <div className="flex items-start gap-3 rounded-[14px] border border-shade-2 bg-surface px-6 py-5">
      <span className={`flex size-8 shrink-0 items-center justify-center rounded-[9px] ${tone}`}>
        {icon}
      </span>
      <div>
        <div className="text-[14px] font-bold leading-[1.3] tracking-[-0.2px] text-ink">{props.title}</div>
        <div className="mt-0.5 text-[12.5px] text-body">{props.hint}</div>
      </div>
    </div>
  );
};

export default PageNotice;
